import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { Air, Favorite, FlashOn, Lens, WarningAmber } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { getAlienPanicText } from '../../common/gameSystems/alien/panicTable';

const useStyles = makeStyles(() => ({
	root: {
		position: 'absolute',
		top: 12,
		right: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		zIndex: 2,
		gap: 6,
	},
	sidebarRoot: {
		top: 8,
		right: 0,
	},
	metric: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
		padding: '6px 8px 6px 10px',
		minHeight: 36,
		minWidth: 144,
		background: 'rgba(8, 14, 13, 0.9)',
		borderTop: '1px solid rgba(122, 145, 135, 0.34)',
		borderBottom: '1px solid rgba(122, 145, 135, 0.34)',
		borderLeft: '3px solid transparent',
		borderRight: 'none',
		fontSize: 12,
		fontWeight: 700,
		fontFamily: '"Oswald", "Roboto Condensed", sans-serif',
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		boxShadow:
			'0 6px 18px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(218, 237, 229, 0.05)',
		clipPath: 'polygon(0 0, 100% 0, 100% 100%, 14px 100%, 0 calc(100% - 14px))',
		pointerEvents: 'auto',
	},
	metricEditable: {
		cursor: 'pointer',
	},
	metricExpanded: {
		boxShadow:
			'0 8px 24px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(238, 247, 242, 0.08), inset 0 0 18px rgba(150, 210, 183, 0.08)',
	},
	warning: {
		color: '#f0bf5a',
		borderLeftColor: '#f0bf5a',
		boxShadow:
			'inset 0 1px 0 rgba(255, 243, 211, 0.08), inset 0 0 18px rgba(240, 191, 90, 0.08)',
	},
	danger: {
		color: '#ff8e8e',
		borderLeftColor: '#d94a4a',
		boxShadow:
			'inset 0 1px 0 rgba(255, 223, 223, 0.05), inset 0 0 18px rgba(217, 74, 74, 0.08)',
	},
	neutral: {
		color: '#d2e0d8',
		borderLeftColor: '#789287',
	},
	left: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		minWidth: 0,
	},
	iconWrap: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 22,
		height: 22,
		border: '1px solid currentColor',
		background: 'rgba(255, 255, 255, 0.03)',
		flexShrink: 0,
	},
	value: {
		minWidth: 34,
		padding: '2px 6px',
		textAlign: 'center',
		border: '1px solid currentColor',
		background: 'rgba(255, 255, 255, 0.04)',
		boxShadow: 'inset 0 0 8px rgba(255, 255, 255, 0.05)',
	},
	label: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	labelWrap: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
	},
	subtitle: {
		fontSize: 10,
		letterSpacing: '0.06em',
		color: 'rgba(245, 248, 246, 0.78)',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	header: {
		alignSelf: 'stretch',
		display: 'flex',
		justifyContent: 'flex-end',
		marginBottom: 2,
	},
	headerText: {
		padding: '1px 8px',
		fontSize: 10,
		letterSpacing: '0.24em',
		fontWeight: 700,
		fontFamily: '"Oswald", "Roboto Condensed", sans-serif',
		background: 'rgba(10, 16, 15, 0.92)',
		color: '#9db8aa',
		borderTop: '1px solid rgba(118, 140, 131, 0.35)',
		borderBottom: '1px solid rgba(118, 140, 131, 0.35)',
		borderLeft: '1px solid rgba(118, 140, 131, 0.35)',
	},
	controls: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
		marginTop: -2,
		padding: '6px 8px',
		minWidth: 144,
		background: 'rgba(8, 14, 13, 0.94)',
		borderTop: '1px solid rgba(122, 145, 135, 0.24)',
		borderBottom: '1px solid rgba(122, 145, 135, 0.34)',
		borderLeft: '3px solid rgba(122, 145, 135, 0.5)',
		clipPath: 'polygon(0 0, 100% 0, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
		pointerEvents: 'auto',
	},
	controlActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
	},
	controlButton: {
		width: 28,
		height: 28,
		border: '1px solid currentColor',
		borderRadius: 0,
		background: 'rgba(255, 255, 255, 0.03)',
		color: 'inherit',
	},
	controlValue: {
		minWidth: 30,
		textAlign: 'center',
		fontSize: 13,
		fontWeight: 700,
	},
	description: {
		marginTop: 6,
		fontSize: 11,
		lineHeight: 1.35,
		letterSpacing: '0.02em',
		color: 'rgba(240, 245, 242, 0.82)',
		textTransform: 'none',
	},
	controlBlock: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		flex: 1,
		minWidth: 0,
	},
	clearButton: {
		border: '1px solid currentColor',
		borderRadius: 0,
		padding: '3px 8px',
		minWidth: 0,
		color: 'inherit',
		fontSize: 10,
		fontWeight: 700,
		letterSpacing: '0.12em',
		background: 'rgba(255, 255, 255, 0.03)',
	},
}));

const ICONS = {
	stress: FlashOn,
	vitality: Favorite,
	air: Air,
	panic: WarningAmber,
	default: Lens,
};

const CameraStatusMetrics = ({
	metrics = [],
	isSideBar = false,
	editable = false,
	onMetricAdjust,
	onMetricClear,
}) => {
	const classes = useStyles();
	const { t, i18n } = useTranslation();
	const [expandedMetricKey, setExpandedMetricKey] = React.useState(null);

	if (!metrics.length) {
		return null;
	}

	const canEditMetric = (metric) => editable && metric?.editable && typeof onMetricAdjust === 'function';
	const canClearMetric = (metric) =>
		editable && metric?.editable && metric?.kind === 'alienActivePanic' && typeof onMetricClear === 'function';

	return (
		<Box
			className={`${classes.root} ${isSideBar ? classes.sidebarRoot : ''}`}
			style={{
				top: isSideBar ? 8 : 12,
				right: 10,
				zIndex: 4,
			}}>
			<Box className={classes.header}>
				<span className={classes.headerText}>CREW STATUS</span>
			</Box>
			{metrics.map((metric) => {
				const Icon = ICONS[metric.icon] || ICONS.default;
				const toneClass = classes[metric.tone] || classes.neutral;
				const metricEditable = canEditMetric(metric);
					const expanded = expandedMetricKey === metric.key;
				const panicText =
					metric.kind === 'alienActivePanic'
						? getAlienPanicText(metric.panicEffectId, i18n.resolvedLanguage || i18n.language)
						: null;
				const metricTitle = panicText?.title || metric.title || null;
				const metricDescription = panicText?.description || metric.description || null;

				return (
					<Box key={metric.key}>
						<Box
							className={`${classes.metric} ${toneClass} ${
								metricEditable ? classes.metricEditable : ''
							} ${expanded ? classes.metricExpanded : ''}`}
							onClick={() => {
								if (!metricEditable) {
									return;
								}

								setExpandedMetricKey((currentKey) =>
									currentKey === metric.key ? null : metric.key
								);
							}}>
							<Box className={classes.left}>
								<Box className={classes.iconWrap}>
									<Icon fontSize="small" />
								</Box>
								<Box className={classes.labelWrap}>
									<span className={classes.label}>{t(metric.labelKey)}</span>
									{metricTitle && <span className={classes.subtitle}>{metricTitle}</span>}
								</Box>
							</Box>
							<span className={classes.value}>{metric.value}</span>
						</Box>
						{metricEditable && expanded && (
							<Box className={`${classes.controls} ${toneClass}`}>
								<Box className={classes.controlBlock}>
									<span className={classes.label}>{t(metric.labelKey)}</span>
									{metricTitle && <span className={classes.subtitle}>{metricTitle}</span>}
									{metricDescription && (
										<span className={classes.description}>{metricDescription}</span>
									)}
								</Box>
								<Box className={classes.controlActions}>
									<IconButton
										size="small"
										type="button"
										className={classes.controlButton}
										onClick={(event) => {
											event.stopPropagation();
											onMetricAdjust(metric, -1);
										}}>
										-
									</IconButton>
									<span className={classes.controlValue}>{metric.value}</span>
									<IconButton
										size="small"
										type="button"
										className={classes.controlButton}
										onClick={(event) => {
											event.stopPropagation();
											onMetricAdjust(metric, 1);
										}}>
										+
									</IconButton>
									{canClearMetric(metric) && (
										<Button
											type="button"
											className={classes.clearButton}
											onClick={(event) => {
												event.stopPropagation();
												onMetricClear(metric);
											}}>
											{t('cameraMetrics.alien.clearPanic')}
										</Button>
									)}
								</Box>
							</Box>
						)}
					</Box>
				);
			})}
		</Box>
	);
};

export default CameraStatusMetrics;
